import { z } from 'zod';

import { CardIdSchema } from '../card/card';
import { CardCategoryIdSchema } from '../card_category/card_category';
import { ProjectIdSchema, SquadIdSchema } from '../project/project';
import {
  CompanyViewSchema,
  CustomerProfileViewSchema,
  FlightLiveStatusSchema,
} from '../traveler/traveler_view';
import { TripViewSchema } from '../traveler/trip_view';
import { HotelLoyaltyProgramsSchema } from '../traveler/hotel_loyalty';

/** Leg lifecycle as the cirium worker records it (`notificationState[i].phase`). */
export const FlightAlertPhaseSchema = z.enum(['scheduled', 'departed', 'landed']);
export const FlightAlertConnectionSchema = z.enum(['none', 'at_risk', 'missed']);

/**
 * Latest `cirium_alerts` row for the leg's referenceNumber — facts, no judgement.
 *
 * TRIP-scoped, not per-leg: `cirium_alerts` rows carry no `legIndex`, so the
 * newest alert of the TRIP (the referenceNumber) is what the cirium read stamps
 * on EVERY leg of that trip. On a multi-leg trip this is therefore not per-leg
 * truth — never present it as "what the traveler was told about THIS leg".
 * Persisting `legIndex` on the alert row and selecting per
 * `(referenceNumber, legIndex)` is the follow-up that makes it per-leg.
 *
 * No `alertType`: the DECIDED message type (`metadata.alertType`, the shared
 * vocabulary the policy may override) lives only in the published envelope and
 * is never persisted on the alert row, so the field could only ever echo
 * `type`. Persisting the decided type is a follow-up.
 */
export const FlightAlertLastAlertSchema = z.object({
  type: z.string(),
  outcome: z.string().nullable(),
  receivedAt: z.coerce.date(),
  notifiedAt: z.coerce.date().nullable(),
});

/**
 * One monitored flight leg with an observed anomaly, as served by
 * cirium `GET /internal/alerts/flights`. UTC ISO strings for instants that
 * the console formats in the airport zone; `live` is the same shape the
 * voucher view carries in `segments[].live`.
 */
export const FlightAlertLegSchema = z.object({
  referenceNumber: z.string().min(1),
  voucherCode: z.string().min(1),
  voucherId: z.string().min(1),
  customerId: z.string().min(1).nullable(),
  companyId: z.string().min(1).nullable(),
  notifyEnabled: z.boolean(),
  legIndex: z.number().int().nonnegative(),
  carrier: z.string().min(2).max(3),
  flightNumber: z.number().int().positive(),
  departureAirport: z.string().length(3),
  arrivalAirport: z.string().length(3),
  importedDepartureUtc: z.iso.datetime(),
  scheduledDepartureUtc: z.iso.datetime(),
  estimatedDepartureUtc: z.iso.datetime().nullable(),
  scheduledArrivalUtc: z.iso.datetime(),
  estimatedArrivalUtc: z.iso.datetime().nullable(),
  /**
   * IANA zone of the departure airport (`airportTimezone` over the IATA code) —
   * what the console formats the departure instants in. `null` when the airport
   * is unknown to the table; defaults to null so a cirium built before the
   * field still validates.
   */
  departureTimeZone: z.string().min(1).nullable().default(null),
  /** Same table, for the ARRIVAL airport — the console formats the arrival instants
   *  in it. `null` when unknown; defaults to null for a cirium built before the field. */
  arrivalTimeZone: z.string().min(1).nullable().default(null),
  live: FlightLiveStatusSchema.nullable(),
  phase: FlightAlertPhaseSchema,
  connectionNotified: FlightAlertConnectionSchema,
  lastAlert: FlightAlertLastAlertSchema.nullable(),
});
export type FlightAlertLeg = z.infer<typeof FlightAlertLegSchema>;

export const FlightAlertsReadResponseSchema = z.object({
  legs: z.array(FlightAlertLegSchema),
  generatedAt: z.coerce.date(),
  /** True when the page ceiling (`MAX_PAGES`) was hit and the trip scan was cut
   *  short — the answer may be missing legs. Defaults false so existing
   *  consumers/fixtures parsing an older response still validate. */
  truncated: z.boolean().default(false),
});
export type FlightAlertsReadResponse = z.infer<typeof FlightAlertsReadResponseSchema>;

// ─── kanban-api ↔ web ────────────────────────────────────────────────────────

export const FlightAlertSeveritySchema = z.enum(['act', 'watch']);
export type FlightAlertSeverity = z.infer<typeof FlightAlertSeveritySchema>;

export const FlightAlertReasonSchema = z.enum([
  'cancelled',
  'diverted',
  'connection_missed',
  'connection_at_risk',
  'delayed',
  'schedule_changed',
]);
export type FlightAlertReason = z.infer<typeof FlightAlertReasonSchema>;

export const FlightAlertNoticeKindSchema = z.enum([
  'notified',
  'shadow',
  'foreign_origin',
  'policy_skip',
  'none',
]);

export const FlightAlertRowSchema = z.object({
  /** `${referenceNumber}:${legIndex}` — stable id for the row and the card link. */
  alertKey: z.string().min(3),
  severity: FlightAlertSeveritySchema,
  reason: FlightAlertReasonSchema,
  traveler: z.object({
    customerId: z.string().min(1),
    companyId: z.string().nullable(),
    name: z.string().nullable(),
    companyName: z.string().nullable(),
    phone: z.string().nullable(),
    vip: z.boolean(),
  }),
  flight: z.object({
    carrier: z.string(),
    flightNumber: z.number().int(),
    departureAirport: z.string(),
    arrivalAirport: z.string(),
    voucherCode: z.string(),
  }),
  departure: z.object({
    scheduledUtc: z.iso.datetime(),
    estimatedUtc: z.iso.datetime().nullable(),
    /** What the list sorts and displays: estimated ?? scheduled. */
    effectiveUtc: z.iso.datetime(),
    /** Departure airport IANA zone to format the instants in ({@link FlightAlertLegSchema}
     *  `departureTimeZone`); `null` → the console falls back to the operator's zone and says so. */
    timeZone: z.string().min(1).nullable().default(null),
  }),
  /**
   * Scheduled/estimated arrival in the ARRIVAL airport's zone ({@link FlightAlertLegSchema}
   * `arrivalTimeZone`). `.optional()` on the wire on purpose: a web deployed ahead of
   * kanban-api must still parse the list — the panel matters more than the arrival cell.
   * `live.estimatedArrivalUtc` stays as it was for the same reason; this block is the one
   * a consumer should read for arrival.
   */
  arrival: z
    .object({
      scheduledUtc: z.iso.datetime(),
      estimatedUtc: z.iso.datetime().nullable(),
      timeZone: z.string().min(1).nullable().default(null),
    })
    .optional(),
  live: z.object({
    status: z.string().nullable(),
    departureGate: z.string().nullable(),
    departureTerminal: z.string().nullable(),
    departureDelayMinutes: z.number().nullable(),
    estimatedArrivalUtc: z.iso.datetime().nullable(),
    observedAt: z.coerce.date().nullable(),
  }),
  /** What the Voll told the traveler, from {@link FlightAlertLastAlertSchema} — so
   *  trip-scoped on a multi-leg trip, and with no decided `alertType` (see there). */
  notice: z.object({
    kind: FlightAlertNoticeKindSchema,
    at: z.coerce.date().nullable(),
    channel: z.string().nullable(),
  }),
  card: z
    .object({
      id: CardIdSchema,
      seq: z.number().int(),
      squadId: SquadIdSchema.nullable(),
      assigneeName: z.string().nullable(),
    })
    .nullable(),
});
export type FlightAlertRow = z.infer<typeof FlightAlertRowSchema>;

export const FlightAlertsResponseSchema = z.object({
  rows: z.array(FlightAlertRowSchema),
  counts: z.object({
    act: z.number().int().nonnegative(),
    watch: z.number().int().nonnegative(),
    vip: z.number().int().nonnegative(),
    unnotified: z.number().int().nonnegative(),
  }),
  generatedAt: z.coerce.date(),
  truncated: z.boolean(),
});
export type FlightAlertsResponse = z.infer<typeof FlightAlertsResponseSchema>;

/**
 * `GET /orgs/:orgId/alerts/flights/:alertKey/trip` — the traveler's trip that holds the
 * alerted voucher, as the app-facing {@link TripViewSchema}. `null` is an answer ("no
 * organized trip carries this voucher"), not an error.
 */
export const FlightAlertTripResponseSchema = z.object({
  trip: TripViewSchema.nullable(),
});
export type FlightAlertTripResponse = z.infer<typeof FlightAlertTripResponseSchema>;

/**
 * `GET /orgs/:orgId/alerts/flights/:alertKey/traveler` — the traveler's operational
 * profile and company, for the operator to have at hand from the panel. The SAME
 * `CustomerProfileView` the customer screen shows (tier-3 raw documents absent by
 * construction — customer-api strips them and this shape has no field for them);
 * the dossier's `trips` is deliberately not carried — the row links the trip itself.
 */
export const FlightAlertTravelerResponseSchema = z.object({
  profile: CustomerProfileViewSchema,
  company: CompanyViewSchema.nullable(),
  // Espelha o dossiê do customer-api; `default(null)` cobre deploy misto (kanban
  // novo lendo customer-api antigo).
  hotelLoyalty: HotelLoyaltyProgramsSchema.nullable().default(null),
});
export type FlightAlertTravelerResponse = z.infer<typeof FlightAlertTravelerResponseSchema>;

export const CreateAlertCardRequestSchema = z.object({
  projectId: ProjectIdSchema,
  squadId: SquadIdSchema,
  categoryId: CardCategoryIdSchema,
});
export type CreateAlertCardRequest = z.infer<typeof CreateAlertCardRequestSchema>;

export const CreateAlertCardResponseSchema = z.object({
  cardId: CardIdSchema,
  squadId: SquadIdSchema.nullable(),
  created: z.boolean(),
});
export type CreateAlertCardResponse = z.infer<typeof CreateAlertCardResponseSchema>;
